import { Fragment, useEffect, useRef, useState } from 'react';

import {
  Box,
  Button,
  ClickAwayListener,
  FormControl,
  Grow,
  MenuItem,
  Paper,
  Popper,
  PopperProps,
  Select,
} from '@mui/material';

import moment from 'moment';

import { TableColumn, TableFilter, TableFilterItem } from '../../Table.types';
import { useColumnFilterPopperStyles } from './ColumnFilterPopper.styles';
import { DateTimeFilterControl } from './DateTimeFilterControl';
import { DualMultiSelectFilterControl } from './DualMultiSelectFilterControl';
import { EnumSearchFilterControl } from './EnumSearchFilterControl';
import { GreaternessNumberFilterControl } from './GreaternessNumberFilterControl';
import { MultiSelectFilterControl } from './MultiSelectFilterControl';
import { TextFilterControl } from './TextFilterControl';

const DATETIME_FORMAT = 'YYYY-MM-DD HH:mm';
const DATE_FORMAT = 'YYYY-MM-DD';

const getDefaultDateFilterValue = (conditionKey: string, pickerType: TableFilter['type']) => {
  if (pickerType === 'datetime') {
    if (conditionKey === 'gte') {
      return moment().startOf('day').format(DATETIME_FORMAT);
    }
    if (conditionKey === 'lte') {
      return moment().endOf('day').format(DATETIME_FORMAT);
    }
  }

  if (pickerType === 'date') {
    if (conditionKey === 'gte') {
      return moment().startOf('day').format(DATE_FORMAT);
    }
    if (conditionKey === 'lte') {
      return moment().endOf('day').format(DATE_FORMAT);
    }
  }

  return null;
};

const hasFilterValues = (filtersState: Record<string, string>) =>
  Object.values(filtersState).some(value => value != null && String(value).trim().length > 0);

const getFiltersFromColumn = (column: TableColumn, currentFilters: Array<TableFilterItem>) => {
  const filters: Record<string, string> = {};
  const columnId = column.alias ?? column.id;

  column.filters?.forEach(filter => {
    if (filter.type === 'dual-multiselect' && filter.columnIds) {
      filter.columnIds.forEach(filterColumnId => {
        const current = currentFilters.find(f => f.columnId === filterColumnId);
        if (current) {
          filters[filterColumnId] = String(current.value);
        }
      });
    } else {
      filter.conditions.forEach(condition => {
        const current = currentFilters.find(
          f => f.condition === condition.key && f.columnId === columnId,
        );

        if (current) {
          filters[condition.key] = String(current.value);
        } else if (filter.type === 'datetime' || filter.type === 'date') {
          const defaultValue = getDefaultDateFilterValue(condition.key, filter.type);
          if (defaultValue) {
            filters[condition.key] = defaultValue;
          }
        }
      });
    }
  });

  return filters;
};

interface ColumnFilterPopperProps extends Omit<PopperProps, 'children'> {
  column: TableColumn;
  activeFilters: Array<TableFilterItem>;
  onClose: (e: any) => void;
  onFilter: (filters: Array<TableFilterItem>) => void;
}

export const ColumnFilterPopper = ({
  column,
  activeFilters,
  onClose,
  onFilter,
  ...popperProps
}: ColumnFilterPopperProps) => {
  const classes = useColumnFilterPopperStyles();
  const selectControlRef = useRef<HTMLDivElement | null>(null);

  const [filters, setFilters] = useState(() => getFiltersFromColumn(column, activeFilters));

  const [isFilterButtonDisabled, setIsFilterButtonDisabled] = useState(
    () => !hasFilterValues(getFiltersFromColumn(column, activeFilters)),
  );

  const areAllFiltersValidMap = new Map<string, boolean>();

  // Change internal filters
  const handleFilterChange = (key, value, isFilterValid?: boolean) => {
    setFilters(prev => {
      if (isFilterValid === undefined) {
        if (value && String(value).trim().length > 0) {
          Object.values(prev)[0] !== value && setIsFilterButtonDisabled(false);
        } else {
          setIsFilterButtonDisabled(true);
        }

        return {
          ...prev,
          [key]: value,
        };
      }

      areAllFiltersValidMap.set(key, isFilterValid);

      const areAllFiltersValid = Array.from(areAllFiltersValidMap, ([key, value]) => ({
        key,
        value,
      })).every(e => e.value);

      setIsFilterButtonDisabled(areAllFiltersValid);

      return {
        ...prev,
        [key]: value,
      };
    });
  };

  // Bubble filters to parent with columnId param
  const handleFilterApply = () => {
    const isDualMultiselect = column.filters?.some(f => f.type === 'dual-multiselect');
    const dualMultiselectColumnIds = isDualMultiselect
      ? column.filters?.find(f => f.type === 'dual-multiselect')?.columnIds || []
      : [];

    const mapped = Object.entries(filters)
      .filter(([, value]) => value && String(value).trim().length > 0) // Filter out empty values
      .map(([condition, value]) => {
        // For dual-multiselect, the "condition" is actually a columnId
        if (isDualMultiselect && dualMultiselectColumnIds.includes(condition)) {
          return {
            columnId: condition,
            condition: 'in',
            value,
          };
        }

        return {
          columnId: column.alias ?? column.id,
          condition,
          value,
        };
      });

    onFilter(mapped);
  };

  useEffect(() => {
    const nextFilters = getFiltersFromColumn(column, activeFilters);
    setFilters(nextFilters);
    setIsFilterButtonDisabled(!hasFilterValues(nextFilters));
  }, [column, activeFilters]);

  const getControlsFromColumn = (column: TableColumn) =>
    column.filters?.map((filter, index) => {
      switch (filter.type) {
        case 'text':
        case 'number': {
          if (filter.type === 'number' && column.filters.length > 1) {
            return (
              <Fragment key={`${column?.id}-${filter.type}Control-${index}`}>
                <GreaternessNumberFilterControl
                  activeFilters={filters}
                  filter={filter}
                  onFilterChange={handleFilterChange}
                  restriction={filter.conditions[0]?.restrictionType}
                  restrictionValue={filters[filter.conditions[0]?.restrictionTargetKey] || null}
                  onClose={onClose}
                  classes={classes}
                />
              </Fragment>
            );
          }

          return (
            <Fragment key={`${column?.id}-${filter.type}Control`}>
              <TextFilterControl
                activeFilters={filters}
                filter={filter}
                columnFilterConditions={column.filters.flatMap(f => f.conditions)}
                onFilterChange={handleFilterChange}
                onClose={onClose}
                classes={classes}
                placeholder={`Filter by ${column.id}`}
              />
            </Fragment>
          );
        }
        case 'datetime': {
          return (
            <DateTimeFilterControl
              key={`${column?.id}-dateControl-${index}`}
              className={classes.filterInput}
              filterKey={filter.conditions[0].key}
              label={filter.label}
              onFilterChange={handleFilterChange}
              value={filters[filter.conditions[0]?.key] ?? ''}
              pickerType="datetime"
              dayBoundary={filter.conditions[0]?.key === 'gte' ? 'start' : 'end'}
              restriction={filter.conditions[0]?.restrictionType}
              restrictionValue={filters[filter.conditions[0]?.restrictionTargetKey] ?? ''}
              disablePast={filter.conditions[0]?.disablePast}
            />
          );
        }
        case 'date': {
          return (
            <DateTimeFilterControl
              key={`${column.id}-dateTimeControl-${index}`}
              className={classes.filterInput}
              filterKey={filter.conditions[0].key}
              onFilterChange={handleFilterChange}
              label={filter.label}
              value={filters[filter.conditions[0]?.key] ?? ''}
              pickerType="date"
              dayBoundary={filter.conditions[0]?.key === 'gte' ? 'start' : 'end'}
              restriction={filter.conditions[0]?.restrictionType}
              restrictionValue={filters[filter.conditions[0]?.restrictionTargetKey] ?? ''}
              disablePast={filter.conditions[0]?.disablePast}
            />
          );
        }
        case 'enum': {
          return (
            <FormControl
              ref={selectControlRef}
              key={`${column?.id}-selectControl-${index}`}
              className={classes.selectFormControl}
            >
              <Select
                data-testid="enum-filter"
                MenuProps={{
                  disablePortal: true,
                  anchorEl: selectControlRef.current,
                  className: classes.selectMenuControl,
                  PaperProps: {
                    style: {
                      width: 'auto',
                      maxWidth: 'none',
                    },
                  },
                  MenuListProps: {
                    style: {
                      whiteSpace: 'nowrap',
                    },
                  },
                }}
                labelId="curation-enum"
                id="curation-enum-select"
                value={filters[filter.conditions[0]?.key] || ''}
                onChange={e => {
                  handleFilterChange(filter.conditions[0]?.key, e.target.value);
                }}
              >
                {filter.values?.map((value, index) => (
                  <MenuItem data-testid={value} key={index} value={value as string}>
                    {String(value)}
                  </MenuItem>
                )) || <MenuItem key="none">No values provided</MenuItem>}
              </Select>
            </FormControl>
          );
        }
        case 'enum-search': {
          return (
            <EnumSearchFilterControl
              key={`${column?.id}-enumSearchControl-${index}`}
              filter={filter}
              activeFilters={filters}
              classes={classes}
              onFilterChange={handleFilterChange}
            />
          );
        }
        case 'multiselect': {
          return (
            <MultiSelectFilterControl
              key={`${column?.id}-multiselectControl-${index}`}
              filter={filter}
              activeFilters={filters}
              classes={classes}
              onFilterChange={handleFilterChange}
            />
          );
        }
        case 'dual-multiselect': {
          return (
            <DualMultiSelectFilterControl
              key={`${column?.id}-dualMultiselectControl-${index}`}
              filter={filter}
              activeFilters={filters}
              classes={classes}
              onFilterChange={handleFilterChange}
            />
          );
        }
        default:
          return null;
      }
    });

  return (
    <Popper
      {...popperProps}
      className={classes.popup}
      placement="bottom"
      transition
      onKeyPress={e => {
        if (e.key === 'Enter') {
          handleFilterApply();
        }
      }}
    >
      {({ TransitionProps, placement }) => (
        <Grow
          {...TransitionProps}
          style={{
            transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
          }}
        >
          <Paper sx={{ px: 2, py: 1, minWidth: 200 }}>
            <ClickAwayListener
              onClickAway={e => {
                onClose(e);
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {getControlsFromColumn(column)}
                <Button
                  data-testid="filter-confirm-button"
                  disabled={isFilterButtonDisabled}
                  color="primary"
                  disableRipple
                  onClick={handleFilterApply}
                  variant="contained"
                  sx={{ mt: 0.5, mb: 0.5 }}
                >
                  Filter
                </Button>
              </Box>
            </ClickAwayListener>
          </Paper>
        </Grow>
      )}
    </Popper>
  );
};
